import { Controller } from "@hotwired/stimulus";

let CropperLib = null;

async function loadCropper() {
  if (CropperLib) return CropperLib;

  const module = await import("cropperjs");
  CropperLib = module.default || module;

  return CropperLib;
}

export default class CropperController extends Controller {
  static targets = ["input", "image", "profileData", "updateButton", "editButton"];

  connect() {
    this.cropper = null;
    this.objectUrl = null;

    this.modal = document.getElementById("cropModal");
    this.okButton = document.getElementById("okButton");
    this.cancelButton = document.getElementById("cancelButton");
    this.cropperImage = document.getElementById("cropperImage");

    this.cancelHandler = this.closeModal.bind(this);
    this.saveHandler = this.saveCroppedImage.bind(this);

    this.cancelButton?.addEventListener("click", this.cancelHandler);
    this.okButton?.addEventListener("click", this.saveHandler);
    this.originalImageUrl = null;
  }

  async getCropper() {
    return this.Cropper || (this.Cropper = await loadCropper());
  }

  async previewImage(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    if (this.hasEditButtonTarget) {
      this.editButtonTarget.classList.add("d-none");
    }

    this.originalImageUrl = null;

    const Cropper = await this.getCropper();

    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }

    this.modal.classList.remove("d-none");
    this.modal.classList.add("d-block", "show");
    document.body.style.overflow = "hidden";

    this.objectUrl = URL.createObjectURL(file);
    this.cropperImage.src = this.objectUrl;
    this.originalImageUrl = this.objectUrl;

    this.cropperImage.onload = () => {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }

      const isCover = this.element.dataset?.cover === "true";

      this.createCropper();
    };
  }

  createCropper() {
    const isCover = this.element.dataset?.cover === "true";

    this.cropper = new this.Cropper(this.cropperImage, {
      viewMode: 1,
      dragMode: isCover ? "move" : "crop",
      cropBoxResizable: !isCover,
      zoomable: !isCover,
      zoomOnWheel: !isCover,
      scalable: !isCover,
      responsive: true,
      autoCropArea: 1,

      ready: () => {
        if (!isCover) {
          this.cropper.setCropBoxData({
            width: 250,
            height: 250
          });
          return;
        }

        const containerData = this.cropper.getContainerData();
        const cropWidth = containerData.width;
        const cropHeight = containerData.height * 0.25;

        this.cropper.setCropBoxData({
          left: 0,
          top: (containerData.height - cropHeight) / 2,
          width: cropWidth,
          height: 250
        });
      }
    });
  }

  saveCroppedImage() {
    if (!this.cropper) return;

    const canvas = this.cropper.getCroppedCanvas({
      imageSmoothingQuality: "high"
    });

    const dataUrl = canvas.toDataURL("image/jpeg", 0.95);

    this.imageTarget.src = dataUrl;
    this.profileDataTarget.value = dataUrl;

    if (this.hasEditButtonTarget) {
      this.editButtonTarget.classList.remove("d-none");
    }

    this.closeModal();
  }

  closeModal() {
    this.modal.classList.remove("show", "d-block");
    document.body.style.overflow = "auto";

    this.resetCropper();
  }

  resetCropper() {
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }

    if (this.objectUrl) {
      this.objectUrl = null;
    }

    if (this.cropperImage) {
      this.cropperImage.src = "";
      this.cropperImage.removeAttribute("src");
    }
  }

  restore() {
    if (this.hasInputTarget) {
      this.inputTarget.value = "";
    }
  }

  disconnect() {
    this.cancelButton?.removeEventListener("click", this.cancelHandler);
    this.okButton?.removeEventListener("click", this.saveHandler);
    this.resetCropper();
  }

  async editImage() {
    if (!this.hasImageTarget || !this.imageTarget.src) return;

    await this.getCropper();

    this.modal.classList.remove("d-none");
    this.modal.classList.add("d-block", "show");
    document.body.style.overflow = "hidden";

    if (!this.originalImageUrl) return;

    this.cropperImage.src = this.originalImageUrl;
    this.cropperImage.onload = () => {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }

      this.createCropper();
    };
  }
}
